import React, { useState, useEffect } from 'react';
import { escapeHtml } from '@/utils/htmlEscape';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Client, getDisplayStatus } from '../types/shelter';
import { User, CreditCard, History, FileText, Printer, CalendarDays, Package } from 'lucide-react';
import UnbeddedPropertyDialog from './UnbeddedPropertyDialog';
// HospitalNameDialog removed — no PHI status captured

import { useAuth } from '../contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRooms } from '@/hooks/useRooms';
import { useSites } from '@/hooks/useSites';
import { useClients } from '@/hooks/useClients';
import {
  ClientProfileSidebar,
  ClientProfileDetails,
  ClientProfileServices,
  ClientProfileHistory,
  ClientProfileNotes,
  ClientProfileBelongings
} from './client-profile';
import { logActivity } from '@/hooks/useActivityLog';

interface ServiceRecord {
  id: string;
  type: 'transit_card' | 'laundry_card' | 'meal' | 'beverage' | 'breakfast' | 'lunch' | 'dinner' | 'milk' | 'juice';
  description: string;
  quantity?: number;
  mealType?: 'breakfast' | 'lunch' | 'dinner';
  timestamp: string;
  staffMember: string;
}

interface Reminder {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  type: 'document' | 'followup';
}

interface ClientProfileProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
  getClientPhoto: (clientId: string) => string;
  onUpdateClient?: (updatedClient: Client) => void;
  onAddServiceRecord?: (clientId: string, service: Omit<ServiceRecord, 'id' | 'timestamp' | 'staffMember'>) => void;
  initialTab?: 'profile' | 'services' | 'history' | 'notes';
  availableRooms?: string[];
  onClearAlert?: (clientId: string, alertText: string, staffName: string) => void;
}

const ClientProfile: React.FC<ClientProfileProps> = ({
  client,
  isOpen,
  onClose,
  getClientPhoto,
  onUpdateClient,
  onAddServiceRecord,
  initialTab = 'profile',
  availableRooms = [],
  onClearAlert
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { archiveClient, isDeleting, updateClient: updateClientHook, clients: liveClients } = useClients();
  // Re-derive client from the live cache so DB updates (e.g. case manager change)
  // are reflected without waiting for the parent to re-pass props.
  const propClient = client;
  client = (propClient && liveClients.find(c => c.id === propClient.id)) || propClient;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [editedClient, setEditedClient] = useState<Client | null>(null);
  const [isRoomAssignmentOpen, setIsRoomAssignmentOpen] = useState(false);
  const [roomAssignmentLoading, setRoomAssignmentLoading] = useState(false);
  const [isPassDialogOpen, setIsPassDialogOpen] = useState(false);
  const [passDialogMode, setPassDialogMode] = useState<'assign' | 'edit'>('assign');
  const [isUnbeddedDialogOpen, setIsUnbeddedDialogOpen] = useState(false);
  
  
  const [passStartDate, setPassStartDate] = useState('');
  const [passEndDate, setPassEndDate] = useState('');
  const [passType, setPassType] = useState<'late' | 'overnight' | 'multi_use'>('overnight');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [printSections, setPrintSections] = useState({
    profile: true,
    services: true,
    alerts: true,
    history: true,
    notes: true
  });
  const [printDateRange, setPrintDateRange] = useState<'all' | 'today' | 'week' | 'month'>('all');

  // Hooks
  const { rooms, isLoading: roomsLoading } = useRooms();
  const { sites } = useSites();

  // Reminders from DB (alerts with category='reminder')
  const { data: dbReminders = [] } = useQuery({
    queryKey: ['client-reminders', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];
      const { data, error } = await supabase
        .from('alerts')
        .select('id, title, description, created_at, type, category')
        .eq('client_id', client.id)
        .eq('status', 'active')
        .eq('category', 'reminder')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((a: any) => ({
        id: a.id,
        title: a.title,
        description: a.description || '',
        dueDate: (a.description || '').match(/Due:\s*([^\n]+)/)?.[1] || a.created_at,
        type: 'followup' as const
      }));
    },
    enabled: !!client?.id && isOpen
  });
  const [reminders, setReminders] = useState<Reminder[]>([]);
  useEffect(() => { setReminders(dbReminders); }, [dbReminders]);

  // Fetch service logs from database
  const { data: dbServiceLogs = [] } = useQuery({
    queryKey: ['client-service-logs', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];

      const { data, error } = await supabase
        .from('service_logs')
        .select(`id, service_type, quantity, notes, created_at, provided_by`)
        .eq('client_id', client.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Service logs query error:', error);
        throw error;
      }
      
      // Fetch staff names
      const staffIds = [...new Set((data || []).map(l => l.provided_by).filter(Boolean))];
      let staffMap = new Map<string, string>();
      if (staffIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', staffIds as string[]);
        staffMap = new Map((profiles || []).map(p => [p.id, p.full_name || 'Unknown']));
      }
      
      return (data || []).map((log: any) => ({ ...log, staffName: staffMap.get(log.provided_by) || 'Unknown' }));
    },
    enabled: !!client?.id && isOpen
  });

  // Map database service logs to UI format
  const serviceRecords: ServiceRecord[] = dbServiceLogs.map((log: any) => {
    const serviceType = log.service_type?.toLowerCase() || '';
    let type: ServiceRecord['type'] = 'meal';
    let description = log.service_type ? log.service_type.charAt(0).toUpperCase() + log.service_type.slice(1) : '';
    
    if (serviceType === 'breakfast') type = 'breakfast';
    else if (serviceType === 'lunch') type = 'lunch';
    else if (serviceType === 'dinner') type = 'dinner';
    else if (serviceType === 'milk') type = 'milk';
    else if (serviceType === 'juice') type = 'juice';
    else if (serviceType.includes('transit') || serviceType.includes('metro')) {
      type = 'transit_card';
      description = 'Transit';
    }
    else if (serviceType.includes('laundry')) type = 'laundry_card';
    else if (serviceType.includes('beverage') || serviceType.includes('drink')) type = 'beverage';
    else if (serviceType.includes('meal') || serviceType.includes('food')) type = 'meal';
    
    return {
      id: log.id,
      type,
      description,
      quantity: log.quantity || 1,
      timestamp: log.created_at,
      staffMember: log.staffName || 'Unknown'
    };
  });

  // Fetch notes from database
  const { data: dbNotes = [], isLoading: notesLoading } = useQuery({
    queryKey: ['client-notes', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];
      
      const { data, error } = await supabase
        .from('notes')
        .select(`id, title, content, note_type, category, created_at, created_by, parent_note_id`)
        .eq('client_id', client.id)
        .is('file_url', null)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Notes query error:', error);
        throw error;
      }
      
      // Fetch profile names for note authors
      const authorIds = [...new Set((data || []).map(n => n.created_by).filter(Boolean))];
      let profileMap = new Map<string, string>();
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', authorIds);
        profileMap = new Map((profiles || []).map(p => [p.id, p.full_name || 'Unknown']));
      }
      
      return (data || [])
        .filter((note: any) => !['property', 'property_return'].includes(note.category || ''))
        .map((note: any) => ({
        id: note.id,
        title: note.title,
        content: note.content,
        author: profileMap.get(note.created_by) || 'Unknown',
        timestamp: note.created_at,
        category: note.note_type || 'general',
        parent_note_id: note.parent_note_id || null
        }));
    },
    enabled: !!client?.id && isOpen
  });

  const clientNotes = dbNotes;

  // Fetch activity logs
  const { data: activityLogs = [] } = useQuery({
    queryKey: ['client-activity-logs', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];

      const { data, error } = await supabase
        .from('user_activity_logs')
        .select(`id, action_type, action_details, created_at, user_id`)
        .contains('action_details', { client_id: client.id })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const userIds = [...new Set((data || []).map(log => log.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      
      const profileMap = new Map((profiles || []).map(p => [p.id, p.full_name]));
      
      return (data || []).map(log => ({
        id: log.id,
        action_type: log.action_type,
        action_details: log.action_details,
        created_at: log.created_at,
        profiles: { full_name: profileMap.get(log.user_id) || 'Unknown' }
      }));
    },
    enabled: !!client?.id && isOpen
  });

  const { data: checkinSessions = [] } = useQuery({
    queryKey: ['client-checkin-sessions', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];

      const { data, error } = await supabase
        .from('checkins')
        .select('id, checked_in_at, checked_out_at, staff_id')
        .eq('client_id', client.id)
        .order('checked_in_at', { ascending: false });

      if (error) throw error;

      const staffIds = [...new Set((data || []).map((row: any) => row.staff_id).filter(Boolean))];
      let profileMap = new Map<string, string>();

      if (staffIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', staffIds as string[]);
        profileMap = new Map((profiles || []).map((profile) => [profile.id, profile.full_name || 'Unknown']));
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        checkedInAt: row.checked_in_at,
        checkedOutAt: row.checked_out_at,
        staffName: profileMap.get(row.staff_id) || 'Unknown',
      }));
    },
    enabled: !!client?.id && isOpen
  });

  // Note mutations
  const createNoteMutation = useMutation({
    mutationFn: async (noteData: { title: string; content: string; category: string; parent_note_id?: string }) => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('notes')
        .insert({ 
          title: noteData.title, 
          content: noteData.content, 
          note_type: noteData.category, 
          client_id: client?.id, 
          site_id: client?.site_id || null, 
          created_by: authUser.id, 
          parent_note_id: noteData.parent_note_id || null 
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', client?.id] });
      toast({ title: 'Note added', description: 'Note has been saved successfully.' });
      
      // Log activity for Today's Activity timeline
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser && client) {
          await logActivity({
            actionType: 'create_note',
            actionDetails: {
              client_id: client.id,
              client_name: client.name,
              note_title: variables.title,
              note_category: variables.category,
              timestamp: new Date().toISOString()
            },
            siteId: client.site_id
          });
          queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });

          // Create an alert so note title shows in alerts system
          await supabase.from('alerts').insert({
            title: variables.title,
            description: variables.content?.substring(0, 200) || '',
            type: 'info',
            category: 'client',
            client_id: client.id,
            site_id: client.site_id || null,
            created_by: authUser.id,
            status: 'active'
          });
          queryClient.invalidateQueries({ queryKey: ['alerts'] });
        }
      } catch (e) {
        console.error('Failed to log note activity:', e);
      }
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to add note', variant: 'destructive' });
    }
  });

  const updateNoteMutation = useMutation({
    mutationFn: async (noteData: { id: string; title: string; content: string; category: string }) => {
      const { data, error } = await supabase
        .from('notes')
        .update({ title: noteData.title, content: noteData.content, note_type: noteData.category })
        .eq('id', noteData.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', client?.id] });
      toast({ title: 'Note updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await supabase.from('notes').delete().eq('id', noteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-notes', client?.id] });
      toast({ title: 'Note deleted' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Effects
  useEffect(() => {
    if (client) setEditedClient(client);
  }, [client]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, isOpen]);

  if (!client) return null;

  // Check if family shelter
  const clientSite = sites.find(s => s.id === client.site_id);
  const isFamilyShelter = clientSite?.is_family_shelter || false;
  const isStaffView = user?.role === 'staff' || user?.role === 'residential_aide';

  // Handlers
  const handleRoomAssignment = async (roomId: string | null) => {
    if (!client?.id) return;
    setRoomAssignmentLoading(true);
    try {
      const selectedRoom = roomId ? rooms.find(r => r.id === roomId) : null;
      
      const { error } = await supabase
          .from('clients')
          .update({ room_id: roomId, updated_at: new Date().toISOString() })
          .eq('id', client.id);
      if (error) throw error;
      
      const base = editedClient || client;
      const updatedClient: Client = { ...base, room: selectedRoom?.room_number || undefined, room_id: roomId || undefined };
      setEditedClient(updatedClient);
      // NOTE: do NOT call onUpdateClient here — that re-runs the full updateClient
      // mutation which rewrites date_of_birth from age and logs a phantom
      // "Profile Updated (Date of Birth)" entry. The DB write + query invalidation
      // below are sufficient for the parent to refresh.

      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      
      // Log room assignment activity
      await logActivity({
        actionType: 'room_assignment',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          room_id: roomId,
          room_number: selectedRoom?.room_number || null,
          timestamp: new Date().toISOString()
        },
        siteId: client.site_id
      });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });

      toast({ title: roomId ? 'Room Assigned' : 'Room Unassigned' });
      setIsRoomAssignmentOpen(false);
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setRoomAssignmentLoading(false);
    }
  };

  const handleSaveEdit = () => {
    if (editedClient && onUpdateClient) onUpdateClient(editedClient);
    setIsEditing(false);
  };

  const handleStatusChange = async (newStatus: 'checked-in' | 'checked-out' | 'unbedded' | 'not_present' | 'reserved' | 'waitlist') => {
    // Intercept unbedded to show property dialog
    if (newStatus === 'unbedded') {
      setIsUnbeddedDialogOpen(true);
      return;
    }
    await executeStatusChange(newStatus);
  };

  const formatForDateTimeLocal = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  const openPassDialog = (mode: 'assign' | 'edit') => {
    const source = editedClient || client;
    setPassDialogMode(mode);
    setPassType((source?.passType as 'late' | 'overnight' | 'multi_use') || 'overnight');
    setPassStartDate(mode === 'edit' ? formatForDateTimeLocal(source?.passStart) : '');
    setPassEndDate(mode === 'edit' ? formatForDateTimeLocal(source?.passEnd) : '');
    setIsPassDialogOpen(true);
  };


  const handleUnbeddedConfirm = async (propertyNotes: string) => {
    setIsUnbeddedDialogOpen(false);
    await executeStatusChange('unbedded', propertyNotes);
  };

  const executeStatusChange = async (
    newStatus: 'checked-in' | 'checked-out' | 'unbedded' | 'not_present' | 'reserved' | 'waitlist',
    propertyNotes?: string,
  ) => {
    if (!editedClient || !client) return;
    const now = new Date().toISOString();
    
    const clearsRoom = newStatus === 'unbedded';
    
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const updatePayload: any = { status: newStatus };
      if (newStatus === 'checked-in') {
        updatePayload.check_in_time = now;
        updatePayload.check_out_time = null;
        updatePayload.pass_start = null;
        updatePayload.pass_end = null;
        updatePayload.pass_type = null;
      }
      else if (newStatus === 'checked-out') updatePayload.check_out_time = now;
      else if (newStatus === 'not_present') updatePayload.not_present_at = now;
      else if (newStatus === 'unbedded') { 
        updatePayload.check_out_time = now; 
        updatePayload.room_id = null;
        updatePayload.unbedded_at = now;
        if (propertyNotes) updatePayload.property_storage_notes = propertyNotes;
      }
      else if (newStatus === 'reserved') updatePayload.reserved_at = now;
      else if (newStatus === 'waitlist') updatePayload.waitlist_at = now;
      
      const { error } = await supabase.from('clients').update(updatePayload).eq('id', client.id);
      if (error) throw error;

      // ── Checkins table consistency ──
      let checkinSiteId: string | null = client.site_id || null;
      if (!checkinSiteId) {
        const { data: siteRow } = await supabase
          .from('clients')
          .select('site_id')
          .eq('id', client.id)
          .single();
        checkinSiteId = siteRow?.site_id || null;
      }

      if (newStatus === 'checked-in') {
        // Close any existing open checkin first
        await supabase
          .from('checkins')
          .update({ checked_out_at: now })
          .eq('client_id', client.id)
          .is('checked_out_at', null);

        // Insert new checkin
        await supabase.from('checkins').insert({
          client_id: client.id,
          site_id: checkinSiteId,
          staff_id: authUser.id,
          checked_in_at: now,
        });

        await supabase
          .from('alerts')
          .update({ status: 'resolved', acknowledged_by: authUser.id, updated_at: now })
          .eq('client_id', client.id)
          .eq('status', 'active')
          .ilike('title', 'Pass Expired:%');
      }

      if (newStatus === 'checked-out' || newStatus === 'unbedded') {
        const { data: openCheckins } = await supabase
          .from('checkins')
          .select('id')
          .eq('client_id', client.id)
          .is('checked_out_at', null)
          .order('checked_in_at', { ascending: false })
          .limit(1);

        if (openCheckins && openCheckins.length > 0) {
          await supabase
            .from('checkins')
            .update({ checked_out_at: now })
            .eq('id', openCheckins[0].id);
        }
      }

      // ── Activity logging ──
      await logActivity({
        actionType: newStatus === 'checked-in' ? 'check_in' : newStatus === 'checked-out' ? 'check_out' : 'client_status_change',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          new_status: newStatus,
          timestamp: now,
        },
        siteId: client.site_id,
      });

      // Notify Case Manager when client is unbedded
      if (newStatus === 'unbedded' && client.id) {
        try {
          const { data: clientData } = await supabase
            .from('clients')
            .select('case_worker_id, first_name, last_name')
            .eq('id', client.id)
            .single();

          if (clientData?.case_worker_id) {
            const clientName = `${clientData.first_name} ${clientData.last_name}`;
            await supabase.from('notifications').insert({
              user_id: clientData.case_worker_id,
              type: 'client_unbedded',
              title: 'Client Unbedded',
              message: `${clientName} has been marked as unbedded${propertyNotes ? ' — property notes recorded' : ''}`,
            });
          }
        } catch (notifErr) {
          console.error('Failed to send unbedded notification:', notifErr);
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
      
      const updatedClient: Client = { 
        ...(editedClient || client), 
        status: newStatus as Client['status'], 
        lastActivity: now,
        ...(newStatus === 'checked-in' ? { checkInTime: now, passStart: undefined, passEnd: undefined, passType: undefined } : {}),
        ...(clearsRoom ? { room: undefined, room_id: undefined } : {}),
        ...(propertyNotes ? { propertyStorageNotes: propertyNotes } : {})
      };
      setEditedClient(updatedClient);
      if (onUpdateClient) onUpdateClient(updatedClient);
      
      const statusLabels: Record<string, string> = {
        'checked-in': 'Checked In',
        'checked-out': 'Checked Out',
        'not_present': 'Marked Not Present (bed kept)',
        'unbedded': 'Marked Unbedded (bed removed)',
        'waitlist': 'Placed on Wait List',
        'reserved': 'Reserved',
      };
      toast({ title: `Client ${statusLabels[newStatus] || newStatus}` });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const handleAssignPass = async () => {
    if (!client || !passStartDate || !passEndDate || !passType) return;
    try {
      const nowIso = new Date().toISOString();
      const passStartIso = new Date(passStartDate).toISOString();
      const passEndIso = new Date(passEndDate).toISOString();

      const updatePayload: any = {
        pass_start: passStartIso,
        pass_end: passEndIso,
        pass_type: passType,
      };

      const { error } = await supabase
        .from('clients')
        .update(updatePayload)
        .eq('id', client.id);
      if (error) throw error;
      
      const updatedClient = {
        ...editedClient!,
        passStart: passStartIso,
        passEnd: passEndIso,
        passType,
      };
      setEditedClient(updatedClient);
      if (onUpdateClient) onUpdateClient(updatedClient);
      
      await supabase
        .from('alerts')
        .update({ status: 'resolved', acknowledged_by: null, updated_at: nowIso })
        .eq('client_id', client.id)
        .eq('status', 'active')
        .ilike('title', 'Pass Expired:%');

      // Log pass assignment/update
      await logActivity({
        actionType: passDialogMode === 'edit' ? 'update_pass' : 'assign_pass',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          previous_pass_start: editedClient?.passStart || client.passStart || null,
          previous_pass_end: editedClient?.passEnd || client.passEnd || null,
          pass_start: passStartIso,
          pass_end: passEndIso,
          pass_type: passType,
          timestamp: nowIso,
        },
        siteId: client.site_id
      });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });

      setIsPassDialogOpen(false);
      setPassStartDate('');
      setPassEndDate('');
      setPassType('overnight');
      toast({ title: passDialogMode === 'edit' ? 'Pass Updated' : 'Pass Assigned' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to assign pass', variant: 'destructive' });
    }
  };

  const handleClearPass = async () => {
    if (!client) return;
    try {
      const { error } = await supabase
        .from('clients')
        .update({ pass_start: null, pass_end: null, pass_type: null } as any)
        .eq('id', client.id);
      if (error) throw error;

      const { data: { user: authUser } } = await supabase.auth.getUser();
      await supabase
        .from('alerts')
        .update({ status: 'resolved', acknowledged_by: authUser?.id || null, updated_at: new Date().toISOString() })
        .eq('client_id', client.id)
        .eq('status', 'active')
        .ilike('title', 'Pass Expired:%');
      
      const updatedClient = { ...editedClient!, passStart: undefined, passEnd: undefined, passType: undefined };
      setEditedClient(updatedClient);
      if (onUpdateClient) onUpdateClient(updatedClient);

      // Log pass clear
      await logActivity({
        actionType: 'clear_pass',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          timestamp: new Date().toISOString()
        },
        siteId: client.site_id
      });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });

      toast({ title: 'Pass Cleared' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to clear pass', variant: 'destructive' });
    }
  };

  const handleDeleteClient = () => {
    if (!client) return;
    archiveClient(client.id);
    setIsDeleteDialogOpen(false);
    onClose();
  };

  const handleAddService = async (service: Omit<ServiceRecord, 'id' | 'timestamp' | 'staffMember'>) => {
    if (!client?.id) return;
    
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      toast({ title: 'Error', description: 'Not authenticated', variant: 'destructive' });
      return;
    }
    
    // Map UI type to database service_type — use granular types directly
    const typeMap: Record<string, string> = {
      transit_card: 'transit',
      laundry_card: 'laundry',
      beverage: 'juice',
    };
    const resolvedType: string = (typeMap[service.type] || service.type).charAt(0).toUpperCase() + (typeMap[service.type] || service.type).slice(1);
    
    try {
      // Ensure site_id is set
      let serviceSiteId = client.site_id || null;
      if (!serviceSiteId) {
        const { data: clientRow } = await supabase
          .from('clients')
          .select('site_id')
          .eq('id', client.id)
          .single();
        serviceSiteId = clientRow?.site_id || null;
      }

      const { error } = await supabase
        .from('service_logs')
        .insert({
          client_id: client.id,
          service_type: resolvedType,
          quantity: service.quantity || 1,
          notes: service.description,
          provided_by: authUser.id,
          site_id: serviceSiteId
        });
      
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['client-service-logs', client.id] });
      queryClient.invalidateQueries({ queryKey: ['service-logs'] });
      queryClient.invalidateQueries({ queryKey: ['all-services-summary'] });

      // Log service activity
      await logActivity({
        actionType: 'add_service',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          service_type: resolvedType,
          quantity: service.quantity || 1,
          timestamp: new Date().toISOString()
        },
        siteId: client.site_id
      });
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });
      
      if (onAddServiceRecord) onAddServiceRecord(client.id, service);
      toast({ title: 'Service Added' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to add service', variant: 'destructive' });
    }
  };

  const handleAcknowledgeReminder = async (id: string) => {
    const reminder = reminders.find(r => r.id === id);
    setReminders(prev => prev.filter(r => r.id !== id));

    // Resolve the alert in DB
    try {
      await supabase.from('alerts').update({ status: 'resolved', updated_at: new Date().toISOString() }).eq('id', id);
      queryClient.invalidateQueries({ queryKey: ['client-reminders', client?.id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    } catch (e) {
      console.error('Failed to resolve alert:', e);
    }
    
    // Log activity for Today's Activity timeline
    if (client) {
      await logActivity({
        actionType: 'acknowledge_reminder',
        actionDetails: {
          client_id: client.id,
          client_name: client.name,
          reminder_text: reminder?.title || 'Unknown reminder',
          timestamp: new Date().toISOString()
        },
        siteId: client.site_id
      });
      
      // Refresh activity logs to show the new entry
      queryClient.invalidateQueries({ queryKey: ['client-activity-logs', client.id] });
    }
    
    toast({ title: 'Reminder Acknowledged' });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Date range filter helper
    const getDateCutoff = () => {
      const now = new Date();
      if (printDateRange === 'today') {
        const start = new Date(now); start.setHours(0,0,0,0); return start;
      }
      if (printDateRange === 'week') {
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }
      if (printDateRange === 'month') {
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }
      return null;
    };
    const cutoff = getDateCutoff();
    const inRange = (dateStr: string) => !cutoff || new Date(dateStr) >= cutoff;

    let sectionsHtml = '';

    // Profile section
    if (printSections.profile) {
      const emergencyHtml = client.emergencyContact
        ? `<tr><td><strong>Emergency Contact</strong></td><td>${client.emergencyContact.name || 'N/A'} (${client.emergencyContact.relationship || 'N/A'}) — ${client.emergencyContact.phone || 'N/A'}</td></tr>`
        : '';
      const childrenHtml = client.children && client.children.length > 0
        ? `<tr><td><strong>Children</strong></td><td>${client.children.map((c: any) => `${c.name}${c.age ? ` (age ${c.age})` : ''}`).join(', ')}</td></tr>`
        : '';

      sectionsHtml += `
        <h2>Basic Information</h2>
        <table>
          <tr><td><strong>Status</strong></td><td><span class="badge ${client.status}">${client.status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown'}</span></td></tr>
          <tr><td><strong>Age</strong></td><td>${client.age || 'N/A'}</td></tr>
          <tr><td><strong>Room</strong></td><td>${client.room || 'Unassigned'}</td></tr>
          <tr><td><strong>Site</strong></td><td>${client.site_name || client.siteName || 'N/A'}</td></tr>
          <tr><td><strong>Case Worker</strong></td><td>${client.caseWorker || 'Unassigned'}</td></tr>
          ${client.isReturnee ? '<tr><td><strong>Returnee</strong></td><td>Yes</td></tr>' : ''}
        </table>
        <h2>Contact Information</h2>
        <table>
          <tr><td><strong>Phone</strong></td><td>${client.phone || 'Not provided'}</td></tr>
          <tr><td><strong>Email</strong></td><td>${client.email || 'Not provided'}</td></tr>
          <tr><td><strong>Address</strong></td><td>${client.address || 'Not provided'}</td></tr>
          ${emergencyHtml}
        </table>
        ${childrenHtml ? `<h2>Additional Details</h2><table>${childrenHtml}</table>` : ''}
        ${client.returneeObservations ? `<h2>Returnee Observations</h2><p>${client.returneeObservations}</p>` : ''}
        ${client.propertyStorageNotes ? `<h2>Property Storage Notes</h2><p>${client.propertyStorageNotes}</p>` : ''}
      `;
    }

    // Services section
    if (printSections.services) {
      const filtered = serviceRecords.filter(s => inRange(s.timestamp));
      const typeLabels: Record<string, string> = { transit_card: 'Transit', laundry_card: 'Laundry Card', meal: 'Meal', beverage: 'Beverage', breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', milk: 'Milk', juice: 'Juice' };
      const rows = filtered.map(s => {
        const label = typeLabels[s.type] || s.type;
        const detail = s.mealType ? ` (${s.mealType})` : '';
        return `<tr><td>${new Date(s.timestamp).toLocaleDateString()}</td><td>${label}${detail}</td><td>${s.quantity || 1}</td><td>${s.staffMember}</td></tr>`;
      }).join('');
      sectionsHtml += `
        <h2>Services (${filtered.length})</h2>
        ${filtered.length > 0
          ? `<table><tr style="background:#f5f5f5"><th style="text-align:left;padding:6px">Date</th><th style="text-align:left;padding:6px">Type</th><th style="text-align:left;padding:6px">Qty</th><th style="text-align:left;padding:6px">Staff</th></tr>${rows}</table>`
          : '<p style="color:#888">No service records found.</p>'
        }
      `;
    }

    // Alerts section
    if (printSections.alerts) {
      const rows = reminders.map(r =>
        `<tr><td>${new Date(r.dueDate).toLocaleDateString()}</td><td>${r.title}</td><td>${r.description}</td></tr>`
      ).join('');
      sectionsHtml += `
        <h2>Active Alerts (${reminders.length})</h2>
        ${reminders.length > 0
          ? `<table><tr style="background:#f5f5f5"><th style="text-align:left;padding:6px">Date</th><th style="text-align:left;padding:6px">Title</th><th style="text-align:left;padding:6px">Description</th></tr>${rows}</table>`
          : '<p style="color:#888">No active alerts.</p>'
        }
      `;
    }

    // History section
    if (printSections.history) {
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
      const SKIP_KEYS = new Set([
        'id', 'client_id', 'user_id', 'site_id', 'room_id', 'alert_id',
        'note_id', 'roster_id', 'inspection_id', 'document_id', 'staff_id',
        'mention_id', 'parent_note_id', 'partner_id', 'case_worker_id',
        'created_at', 'updated_at', 'timestamp', 'time', 'checked_in_at',
        'checked_out_at', 'acknowledged_at', 'reported_at', 'resolved_at',
      ]);
      const filtered = activityLogs.filter((l: any) => inRange(l.created_at));
      const rows = filtered.map((l: any) => {
        const action = (l.action_type || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        const details = l.action_details ? Object.entries(l.action_details as Record<string, any>)
          .filter(([k, v]) => {
            if (v == null || v === '') return false;
            if (SKIP_KEYS.has(k)) return false;
            const str = String(v);
            if (UUID_RE.test(str)) return false;
            if (ISO_DATE_RE.test(str)) return false;
            return true;
          })
          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
          .join(', ') : '';
        return `<tr><td>${new Date(l.created_at).toLocaleDateString()}</td><td>${action}</td><td>${details}</td><td>${l.profiles?.full_name || 'Unknown'}</td></tr>`;
      }).join('');
      sectionsHtml += `
        <h2>Activity History (${filtered.length})</h2>
        ${filtered.length > 0
          ? `<table><tr style="background:#f5f5f5"><th style="text-align:left;padding:6px">Date</th><th style="text-align:left;padding:6px">Action</th><th style="text-align:left;padding:6px">Details</th><th style="text-align:left;padding:6px">Staff</th></tr>${rows}</table>`
          : '<p style="color:#888">No activity records found.</p>'
        }
      `;
    }

    // Notes section
    if (printSections.notes) {
      const filtered = clientNotes.filter((n: any) => inRange(n.timestamp));
      const noteBlocks = filtered.map((n: any) =>
        `<div style="border:1px solid #ddd;padding:10px;margin-bottom:8px;border-radius:4px">
          <div style="display:flex;justify-content:space-between;margin-bottom:4px">
            <strong>${n.title}</strong>
            <span style="color:#888;font-size:12px">${new Date(n.timestamp).toLocaleDateString()} — ${n.author}</span>
          </div>
          <span style="display:inline-block;padding:1px 6px;border-radius:3px;font-size:11px;background:#eee;margin-bottom:6px">${n.category || 'general'}</span>
          <p style="margin:4px 0;white-space:pre-wrap">${(n.content || '').replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')}</p>
        </div>`
      ).join('');
      sectionsHtml += `
        <h2>Notes (${filtered.length})</h2>
        ${filtered.length > 0 ? noteBlocks : '<p style="color:#888">No notes found.</p>'}
      `;
    }

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Client Profile - ${escapeHtml(client?.name)}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:30px;color:#222}
        .header{border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:20px}
        h1{margin:0 0 5px}
        h2{margin:25px 0 10px;border-bottom:1px solid #ccc;padding-bottom:5px}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        td,th{padding:6px 10px;border-bottom:1px solid #eee;vertical-align:top}
        td:first-child{width:180px;font-weight:500;color:#555}
        .badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:bold}
        .checked-in{background:#d4edda;color:#155724}
        .checked-out{background:#f8d7da;color:#721c24}
        .footer{margin-top:30px;padding-top:10px;border-top:1px solid #000;font-size:11px;text-align:center;color:#666}
      </style>
    </head><body>
      <div class="header">
        <h1>Client Profile Report</h1>
        <p><strong>Client:</strong> ${client.name}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}${printDateRange !== 'all' ? ` — Date Range: ${printDateRange}` : ''}</p>
      </div>
      ${sectionsHtml}
      <div class="footer"><p>Confidential — For Internal Use Only</p></div>
    </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="mx-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:max-w-5xl max-h-[95vh] sm:max-h-[90vh] p-0 overflow-hidden bg-background border border-border">
          <div className="flex flex-col lg:flex-row h-[90vh] lg:h-[85vh] min-h-0">
            {/* Sidebar */}
            <ClientProfileSidebar
              client={client}
              editedClient={editedClient}
              getClientPhoto={getClientPhoto}
              userRole={user?.role}
              rooms={rooms}
              sites={sites}
              roomsLoading={roomsLoading}
              roomAssignmentLoading={roomAssignmentLoading}
              onStatusChange={handleStatusChange}
              onAssignPass={() => openPassDialog('assign')}
              onEditPass={() => openPassDialog('edit')}
              onClearPass={handleClearPass}
              onRoomAssignmentOpen={() => setIsRoomAssignmentOpen(true)}
              onRoomAssignment={handleRoomAssignment}
              onPrint={() => setIsPrintDialogOpen(true)}
              onDelete={() => setIsDeleteDialogOpen(true)}
              isDeleting={isDeleting}
            />

            {/* Main Content */}
            <div className="flex-1 flex min-h-0 flex-col overflow-hidden">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)} className="flex-1 min-h-0 flex flex-col">
                <div className="border-b border-border px-2 sm:px-4 pt-3 sm:pt-4">
                  <TabsList className="grid w-full grid-cols-5 h-auto">
                    <TabsTrigger value="profile" className="flex-col gap-0.5 py-2 px-1 sm:flex-row sm:gap-1 sm:px-3"><User className="h-4 w-4" /><span className="text-[10px] sm:text-sm leading-none">Details</span></TabsTrigger>
                    <TabsTrigger value="services" className="flex-col gap-0.5 py-2 px-1 sm:flex-row sm:gap-1 sm:px-3"><CreditCard className="h-4 w-4" /><span className="text-[10px] sm:text-sm leading-none">Services</span></TabsTrigger>
                    <TabsTrigger value="notes" className="flex-col gap-0.5 py-2 px-1 sm:flex-row sm:gap-1 sm:px-3"><FileText className="h-4 w-4" /><span className="text-[10px] sm:text-sm leading-none">Notes</span></TabsTrigger>
                    <TabsTrigger value="history" className="flex-col gap-0.5 py-2 px-1 sm:flex-row sm:gap-1 sm:px-3"><History className="h-4 w-4" /><span className="text-[10px] sm:text-sm leading-none">History</span></TabsTrigger>
                    <TabsTrigger value="belongings" className="flex-col gap-0.5 py-2 px-1 sm:flex-row sm:gap-1 sm:px-3"><Package className="h-4 w-4" /><span className="text-[10px] sm:text-sm leading-none">Belongings</span></TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-4">
                  <TabsContent value="profile" className="mt-0">
                    <ClientProfileDetails
                      client={client}
                      editedClient={editedClient}
                      isEditing={isEditing}
                      isStaffView={isStaffView}
                      reminders={reminders}
                      isFamilyShelter={isFamilyShelter}
                      onEdit={() => setIsEditing(true)}
                      onSave={handleSaveEdit}
                      onEditedClientChange={setEditedClient}
                      onAcknowledgeReminder={handleAcknowledgeReminder}
                    />
                  </TabsContent>

                  <TabsContent value="services" className="mt-0">
                    <ClientProfileServices
                      client={client}
                      userRole={user?.role}
                      userName={user?.name}
                      serviceRecords={serviceRecords}
                      onAddService={handleAddService}
                    />
                  </TabsContent>

                  <TabsContent value="belongings" className="mt-0">
                    <ClientProfileBelongings client={client} />
                  </TabsContent>

                  <TabsContent value="history" className="mt-0">
                    <ClientProfileHistory
                      client={client}
                      activityLogs={activityLogs}
                      serviceRecords={serviceRecords}
                      checkinSessions={checkinSessions}
                    />
                  </TabsContent>

                  <TabsContent value="notes" className="mt-0">
                    <ClientProfileNotes
                      client={client}
                      notes={clientNotes}
                      notesLoading={notesLoading}
                      onAddNote={(note) => createNoteMutation.mutate(note)}
                      onUpdateNote={(note) => updateNoteMutation.mutate(note)}
                      onDeleteNote={(id) => deleteNoteMutation.mutate(id)}
                      isAddingNote={createNoteMutation.isPending}
                      isUpdatingNote={updateNoteMutation.isPending}
                      isDeletingNote={deleteNoteMutation.isPending}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unbedded Property Dialog */}
      <UnbeddedPropertyDialog
        isOpen={isUnbeddedDialogOpen}
        onClose={() => setIsUnbeddedDialogOpen(false)}
        onConfirm={handleUnbeddedConfirm}
        clientName={client.name}
        existingNotes={editedClient?.propertyStorageNotes || client.propertyStorageNotes}
      />

      {/* Hospital status removed — no PHI captured */}


      {/* Archive Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive <strong>{client.name}</strong>? They will be removed from active lists but their records will be preserved. You can restore them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Archive Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pass Assignment Dialog */}
      <Dialog open={isPassDialogOpen} onOpenChange={setIsPassDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              {passDialogMode === 'edit' ? 'Edit Pass' : 'Assign Pass'}
            </h3>
            <div className="grid gap-4">
              <div>
                <Label>Pass Type</Label>
                <Select value={passType} onValueChange={(v) => setPassType(v as 'late' | 'overnight' | 'multi_use')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="late">Late — same-day return after curfew</SelectItem>
                    <SelectItem value="overnight">Overnight — 1 night away</SelectItem>
                    <SelectItem value="multi_use">Multi-use — multiple nights</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start Date & Time</Label>
                <Input type="datetime-local" value={passStartDate} onChange={(e) => setPassStartDate(e.target.value)} />
              </div>
              <div>
                <Label>End Date & Time</Label>
                <Input type="datetime-local" value={passEndDate} onChange={(e) => setPassEndDate(e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsPassDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignPass} disabled={!passStartDate || !passEndDate || !passType}>
                {passDialogMode === 'edit' ? 'Update Pass' : 'Assign Pass'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Assignment Dialog */}
      <Dialog open={isRoomAssignmentOpen} onOpenChange={setIsRoomAssignmentOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Change Room Assignment</h3>
            <Select onValueChange={(value) => handleRoomAssignment(value === 'unassign' ? null : value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a room..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassign">Unassign Room</SelectItem>
                {rooms
                  .filter(r => r.site_id === client.site_id)
                  .sort((a, b) => (a.room_number || '').localeCompare(b.room_number || '', undefined, { numeric: true }))
                  .map((room) => {
                    const occupancy = room.current_occupancy || 0;
                    const capacity = room.capacity || 1;
                    const isCurrent = client.room_id === room.id || client.room === room.room_number;
                    const isFull = occupancy >= capacity && !isCurrent;
                    return (
                      <SelectItem key={room.id} value={room.id} disabled={isFull}>
                        <span className="flex items-center gap-2">
                          <span>{room.room_number}{isCurrent && <span className="text-primary ml-1">(Current)</span>}</span>
                          <span className={`text-xs ${isFull ? 'text-destructive' : 'text-muted-foreground'}`}>
                            ({occupancy}/{capacity}){isFull ? ' Full' : ''}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={isPrintDialogOpen} onOpenChange={setIsPrintDialogOpen}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Print Client Profile
            </h3>
            <div className="space-y-3">
              {Object.entries(printSections).map(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox id={key} checked={value} onCheckedChange={(checked) => setPrintSections(prev => ({ ...prev, [key]: !!checked }))} />
                  <Label htmlFor={key} className="capitalize">{key} Information</Label>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <Label className="text-sm font-medium">Date Range</Label>
              <Select value={printDateRange} onValueChange={(v: any) => setPrintDateRange(v)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsPrintDialogOpen(false)}>Cancel</Button>
              <Button onClick={() => { handlePrint(); setIsPrintDialogOpen(false); }}>
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientProfile;
